import { Component } from 'react';
import { withTranslation } from 'react-i18next';
import Loading from '../../../components/Loading';
import ReactModal from '../../../components/Modal';
import ErrorMessage from '../ErrorMessage';
import ProjectMarketModal from '../projectSettings/ProjectMarketModal';
import { MISSING_MARKET_CODE, generateBrandProfile } from '../projectSettings/query';
import { STEP_PROFILE, TOTAL_STEPS } from './state';

const PREFIX = 'ai_tracking.onboarding';
const PREVIEW_LIMIT = 6;

/**
 * Step 2: profile the brand, which the user never has to ask for.
 *
 * Step 1 has just saved a domain, and the only thing anyone would do next is profile it, so this
 * starts the moment it opens rather than offering a button that has no alternative. Generation
 * is a synchronous AI call on the server and takes a while, so the wait is named ("Analyzing
 * acme.com") instead of being a bare spinner, and closing is kept out of reach while it runs.
 *
 * A project whose earlier profiling attempt failed still has a profile row, and the API answers
 * a plain request for one with 409 even though the project reports no profile. Retrying that
 * single case with `force` is what keeps a failed first attempt from becoming permanent.
 *
 * Profiling also needs a market, and the project may not have one -- step 1 fills it in, but a
 * project that already had a brand and domain can arrive here straight from the checklist with
 * no country or language ever set. The API refuses that with `project_country_language_empty`,
 * which is a question with an answer rather than a failure, so the same country/language modal
 * the settings page uses opens on the spot and generation resumes once it is saved.
 */
class StepProfile extends Component {
    constructor() {
        super();

        this.onGenerate = this.onGenerate.bind(this);
        this.onMarketSaved = this.onMarketSaved.bind(this);
        this.onMarketModalClose = this.onMarketModalClose.bind(this);

        this.state = {
            generating: true,
            error: '',
            profile: null,
            isMarketModalOpen: false,
        };
    }

    componentDidMount() {
        this.onGenerate();
    }

    componentWillUnmount() {
        this.unmounted = true;
    }

    async onGenerate() {
        const { t, project } = this.props;

        this.setState({ generating: true, error: '', isMarketModalOpen: false });

        try {
            let res = await generateBrandProfile(project?.id);

            // 409 means a profile row is already there. The project told us it has none, so the
            // row is a failed or half-finished run, and re-profiling over it is exactly right.
            if (res.status === 409) {
                res = await generateBrandProfile(project?.id, true);
            }

            const data = await res.json();
            if (this.unmounted) return;

            if (res.status !== 201) {
                // The reason stays on the step either way: it is the whole story for any other
                // failure, and still worth reading if the market modal is dismissed rather
                // than filled in.
                this.setState({
                    generating: false,
                    error: data?.detail ?? t('global.something_went_wrong'),
                    isMarketModalOpen: data?.code === MISSING_MARKET_CODE,
                });
                return;
            }

            this.setState({ generating: false, profile: data });
            this.props.onProfiled?.(data);
        } catch (err) {
            if (this.unmounted) return;
            this.setState({
                generating: false,
                error: err?.message ?? t('global.something_went_wrong'),
            });
        }
    }

    /** The project has a market now, so the request it refused can be made again. */
    onMarketSaved(project) {
        this.setState({ isMarketModalOpen: false });
        this.props.onProjectUpdated?.(project);
        this.onGenerate();
    }

    onMarketModalClose() {
        this.setState({ isMarketModalOpen: false });
    }

    renderPreview() {
        const { t } = this.props;
        const { profile } = this.state;
        const attributes = (profile?.brand_attributes ?? []).slice(0, PREVIEW_LIMIT);
        const products = (profile?.products_services ?? []).slice(0, PREVIEW_LIMIT);

        return (
            <div className="onboarding-profile-preview">
                {!!profile?.industry && (
                    <div className="onboarding-profile-industry">{profile.industry}</div>
                )}
                {!!profile?.description && (
                    <p className="onboarding-profile-description">{profile.description}</p>
                )}
                {products.length > 0 && (
                    <div className="onboarding-profile-group">
                        <div className="onboarding-profile-group-title">
                            {t(`${PREFIX}.profile.products`)}
                        </div>
                        <div className="onboarding-profile-tags">
                            {products.map((item) => (
                                <span key={item} className="onboarding-profile-tag">
                                    {item}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
                {attributes.length > 0 && (
                    <div className="onboarding-profile-group">
                        <div className="onboarding-profile-group-title">
                            {t(`${PREFIX}.profile.attributes`)}
                        </div>
                        <div className="onboarding-profile-tags">
                            {attributes.map((item) => (
                                <span key={item} className="onboarding-profile-tag">
                                    {item}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
                <div className="onboarding-profile-note">{t(`${PREFIX}.profile.editable`)}</div>
            </div>
        );
    }

    render() {
        const { t, project, isOpen, onContinue, onClose } = this.props;
        const { generating, error, profile } = this.state;

        return (
            <>
                <ReactModal
                    modalState={isOpen}
                    className="width-sm ai-tracking-modal onboarding-step onboarding-profile-step"
                    onClose={generating ? () => {} : onClose}
                >
                    <h3>
                        {t(`${PREFIX}.step_header`, {
                            current: STEP_PROFILE,
                            total: TOTAL_STEPS,
                            name: t(`${PREFIX}.steps.profile`),
                        })}
                    </h3>
                    <p className="as-page-subtitle">{t(`${PREFIX}.profile.subtitle`)}</p>
                    <div className="ai-tracking-modal-body">
                        {generating && (
                            <div className="onboarding-profile-waiting">
                                <div className="d-flex gap-8 align-items-center">
                                    <strong>
                                        {t(`${PREFIX}.profile.analyzing`, {
                                            domain: project?.own_domain,
                                        })}
                                    </strong>
                                    <Loading />
                                </div>
                                <div className="onboarding-profile-note mt-10">
                                    {t(`${PREFIX}.profile.analyzing_hint`)}
                                </div>
                            </div>
                        )}
                        {!!error && <ErrorMessage message={error} />}
                        {!generating && !!profile && this.renderPreview()}
                    </div>
                    <div className="ai-tracking-modal-footer">
                        {profile ? (
                            <button
                                type="button"
                                className="button button-filled button-primary width-sm btn"
                                onClick={onContinue}
                            >
                                {t(`${PREFIX}.continue`)}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="button button-filled button-primary width-sm btn d-flex gap-5 align-items-center justify-content-center"
                                disabled={generating}
                                onClick={this.onGenerate}
                            >
                                {generating && <Loading onlyPreloader />}
                                {generating
                                    ? t(`${PREFIX}.profile.generating`)
                                    : t(`${PREFIX}.profile.retry`)}
                            </button>
                        )}
                        <button
                            type="button"
                            className="button button-outline button-primary width-sm btn"
                            disabled={generating}
                            onClick={onClose}
                        >
                            {t(`${PREFIX}.finish_later`)}
                        </button>
                    </div>
                </ReactModal>
                {/* A sibling, not a child: two react-modals nested in one tree fight over the
                    focus trap. */}
                {this.state.isMarketModalOpen && (
                    <ProjectMarketModal
                        isOpen
                        project={project}
                        onSaved={this.onMarketSaved}
                        onClose={this.onMarketModalClose}
                    />
                )}
            </>
        );
    }
}

export default withTranslation()(StepProfile);
